/**
 * Grid bookcase designs, presets and the change model (moved from src/domain/design.ts,
 * plan §4.6). Every change function is pure and returns a new design.
 *
 * A grid design is the first slice's `DesignInput` widened with optional fields: `bays`,
 * `bayWidths` (clear widths), `bayLayouts` (bays 2…N), `back`, `plinth`, `joinery`. The top
 * level `n`, `even`, `openings` are bay 1, so a one-bay design with an overlay back and no
 * plinth carries none of the new fields and is exactly the first slice's design.
 */
import type { Appearance, BayLayout, Design } from '../../domain/schemas';
import { CONSTRUCTION, STOCK, limitsOf, type ConstructionProfile, type StockProfile } from '../../domain/profiles';
import { mm, sum } from '../../domain/units';
import { isBoardStock, resolveStock, stockById, type AnyStock } from '../stock';
import { BASE_KEYS, baseOf, footKindFor, liftOf, setBaseParam, type BaseSpec } from '../base';

export const FAMILY = 'grid-bookcase';
/** The first slice's family id; saved projects still carry it. */
export const LEGACY_FAMILY = 'single-bay-open-bookcase';

export type PresetKey = 'hylla' | 'even' | 'residual' | 'multibay';

export interface Preset {
  key: PresetKey;
  label: string;
  description: string;
  W: number;
  H: number;
  D: number;
  n: number;
  even: boolean;
  openings: number[] | null;
  bays?: number;
  plinth?: number;
}

export const PRESETS: Readonly<Record<PresetKey, Readonly<Preset>>> = Object.freeze({
  hylla: Object.freeze({
    key: 'hylla', label: 'Hylla (uneven)', description: '900 × 1800 × 320, 4 shelves, uneven',
    W: mm(900), H: mm(1800), D: mm(320), n: 4, even: false,
    openings: [mm(400), mm(380), mm(330), mm(300), mm(282)],
  }),
  even: Object.freeze({
    key: 'even', label: 'Even 800 × 1200', description: '800 × 1200 × 300, 3 shelves',
    W: mm(800), H: mm(1200), D: mm(300), n: 3, even: true, openings: null,
  }),
  residual: Object.freeze({
    key: 'residual', label: 'Residual 800 × 1200, 6 shelves', description: '800 × 1200 × 300, 6 shelves',
    W: mm(800), H: mm(1200), D: mm(300), n: 6, even: true, openings: null,
  }),
  multibay: Object.freeze({
    key: 'multibay', label: 'Three bays 1800 × 1400', description: '1800 × 1400 × 300, 3 bays of 3 shelves, 40 mm plinth',
    W: mm(1800), H: mm(1400), D: mm(300), n: 3, even: true, openings: null, bays: 3, plinth: mm(40),
  }),
});

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ name: 'Hylla, my version', staging: 'studio', props: true });

// ---------------------------------------------------------------------------------------------
// Stock and layout helpers
// ---------------------------------------------------------------------------------------------

/**
 * The stock a design is built from: the design's own `stockProfileId` when it is bundled,
 * except that a passed profile with the same id wins (a maker's measured thickness from profiles/).
 */
export function stockFor(design: Pick<Design, 'stockProfileId'>, stock?: AnyStock | null): AnyStock {
  if (stock && stock.id === design.stockProfileId) return stock;
  return stockById(design.stockProfileId) ?? stock ?? STOCK;
}

export const thicknessOf = (s: AnyStock): number => resolveStock(s).thicknessTicks;

export const bayCount = (d: Pick<Design, 'bays'>): number => Math.max(1, d.bays ?? 1);

/** Bay `bay`'s shelf count, Even flag and openings (bay 0 is the top level). */
export function layoutOf(d: Design, bay = 0): BayLayout {
  if (bay <= 0) return { n: d.n, even: d.even, openings: d.openings };
  const l = d.bayLayouts?.[bay - 1];
  return l ?? { n: d.n, even: d.even, openings: d.openings.slice() };
}

/** A new design with bay `bay`'s layout replaced. */
export function withLayout(d: Design, bay: number, layout: BayLayout): Design {
  const out = cloneDesign(d);
  if (bay <= 0) {
    out.n = layout.n;
    out.even = layout.even;
    out.openings = layout.openings.slice();
    return out;
  }
  const layouts = (out.bayLayouts ?? []).slice();
  while (layouts.length < bay) layouts.push({ ...layoutOf(d, layouts.length + 1), openings: layoutOf(d, layouts.length + 1).openings.slice() });
  layouts[bay - 1] = { n: layout.n, even: layout.even, openings: layout.openings.slice() };
  out.bayLayouts = layouts;
  return out;
}

/** Every bay's layout, left to right. */
export const layoutsOf = (d: Design): BayLayout[] => Array.from({ length: bayCount(d) }, (_x, i) => layoutOf(d, i));

/** Total clear width shared by the bays: W − (bays + 1)t. */
export const clearWidthTicks = (d: Pick<Design, 'W' | 'bays'>, t: number): number => d.W - (bayCount(d) + 1) * t;

/** Bay clear widths, left to right (one bay: W − 2t). */
export function bayWidthsOf(d: Design, stock?: AnyStock | null): number[] {
  const t = thicknessOf(stockFor(d, stock));
  const n = bayCount(d);
  if (n === 1) return [d.W - 2 * t];
  if (d.bayWidths && d.bayWidths.length === n) return d.bayWidths.slice();
  return evenOpenings(clearWidthTicks(d, t), n);
}

/**
 * Split `availableTicks` into `count` integer openings; the residual ticks go
 * one each to the lowest openings first (bottom-up).
 */
export function evenOpenings(availableTicks: number, count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(availableTicks / count);
  let residual = availableTicks - base * count;
  const out = new Array<number>(count).fill(base);
  for (let i = 0; i < count && residual > 0; i++, residual--) out[i]! += 1;
  return out;
}

/**
 * What a grid design stands on (FND-08): `design.base` with the plinth height in `plinth`;
 * a design without `base` is on a plinth when `plinth > 0`, else on the floor (the first slice's).
 */
export const gridBaseOf = (d: Partial<Pick<Design, 'base' | 'plinth' | 'plinthSetback' | 'feetHeight' | 'toeHeight' | 'stockProfileId'>>): BaseSpec => {
  if (d.base !== 'feet') return baseOf(d);
  const s = d.stockProfileId ? stockById(d.stockProfileId) : null;
  return baseOf(d, footKindFor(s && isBoardStock(s) ? 'board' : 'sheet'));
};

/** Height the carcass is lifted by: the plinth or the feet (turned feet under solid timber, levelling feet under ply), ticks. */
export const liftTicks = (d: Partial<Pick<Design, 'base' | 'plinth' | 'plinthSetback' | 'feetHeight' | 'toeHeight' | 'stockProfileId'>>): number => liftOf(gridBaseOf(d));

/** Available clear height in a bay: A = H − lift (plinth or feet) − (n + 2)t, ticks. */
export function availableTicks(design: Pick<Design, 'H' | 'n'> & Partial<Pick<Design, 'plinth' | 'stockProfileId' | 'base' | 'feetHeight' | 'toeHeight'>>, stock: AnyStock = STOCK): number {
  const s = design.stockProfileId ? stockFor(design as Design, stock) : stock;
  return design.H - liftTicks(design) - (design.n + 2) * thicknessOf(s);
}

/** Bay `bay`'s available clear height. */
export function bayAvailableTicks(d: Design, bay = 0, stock?: AnyStock | null): number {
  const s = stockFor(d, stock);
  return d.H - liftTicks(d) - (layoutOf(d, bay).n + 2) * thicknessOf(s);
}

// ---------------------------------------------------------------------------------------------
// Creation
// ---------------------------------------------------------------------------------------------

/** Create a design from a preset key ('hylla' | 'even' | 'residual' | 'multibay'). */
export function createDesign(presetKey: PresetKey = 'hylla', stock: AnyStock = STOCK, construction: ConstructionProfile = CONSTRUCTION): Design {
  const p = PRESETS[presetKey];
  if (!p) throw new Error(`Unknown preset ${String(presetKey)}`);
  const t = thicknessOf(stock);
  const plinth = p.plinth ?? 0;
  const available = p.H - plinth - (p.n + 2) * t;
  const base: Design = {
    family: FAMILY,
    W: p.W, H: p.H, D: p.D, n: p.n, even: p.even,
    openings: p.openings ? p.openings.slice() : evenOpenings(available, p.n + 1),
    stockProfileId: stock.id,
    constructionProfileId: construction.id,
    appearance: { ...DEFAULT_APPEARANCE },
  };
  if (plinth) base.plinth = plinth;
  if (p.bays && p.bays > 1) {
    base.bays = p.bays;
    base.bayWidths = evenOpenings(p.W - (p.bays + 1) * t, p.bays);
    base.bayLayouts = Array.from({ length: p.bays - 1 }, () => ({ n: p.n, even: p.even, openings: base.openings.slice() }));
  }
  return base;
}

export function cloneDesign(d: Design): Design {
  const out: Design = { ...d, openings: d.openings.slice(), appearance: { ...d.appearance } };
  if (d.fields) out.fields = JSON.parse(JSON.stringify(d.fields)) as Design['fields'];
  if (d.bayWidths) out.bayWidths = d.bayWidths.slice();
  if (d.bayLayouts) out.bayLayouts = d.bayLayouts.map((l) => ({ ...l, openings: l.openings.slice() }));
  if (d.params) out.params = JSON.parse(JSON.stringify(d.params)) as Record<string, unknown>;
  return out;
}

// ---------------------------------------------------------------------------------------------
// Openings (the grid family's behaviours: even, rebalance, remainder)
// ---------------------------------------------------------------------------------------------

export type Rebalance = 'neighbour' | 'none';

export interface ChangeOptions {
  rebalance?: Rebalance;
  stock?: AnyStock;
  construction?: ConstructionProfile;
  /** 0-based bay (default 0, the first). */
  bay?: number;
}

/**
 * Set opening `index` (0-based, bottom-up) of a bay to `ticks`.
 * rebalance 'neighbour' (default): the opening above absorbs the difference
 *   (the one below for the top opening), clamped so both stay ≥ the minimum opening.
 *   This is "moving a shelf".
 * rebalance 'none': only this opening changes; the remainder shows in validation.
 */
export function setOpening(design: Design, index: number, ticks: number, { rebalance = 'neighbour', construction = CONSTRUCTION, bay = 0 }: ChangeOptions = {}): Design {
  const lay = layoutOf(design, bay);
  const g = lay.openings.slice();
  if (index < 0 || index >= g.length) return cloneDesign(design);
  ticks = Math.round(ticks);
  if (rebalance === 'neighbour' && g.length > 1) {
    const j = index < g.length - 1 ? index + 1 : index - 1;
    const min = construction.envelope.minOpeningTicks;
    const total = g[index]! + g[j]!;
    const v = Math.max(min, Math.min(total - min, ticks));
    g[index] = v;
    g[j] = total - v;
  } else {
    g[index] = ticks;
  }
  return withLayout(design, bay, { n: lay.n, even: false, openings: g });
}

/**
 * The largest value opening `index` can take under 'neighbour' rebalance
 * (`g_i + g_j − g_min`), or null when there is no partner opening.
 */
export function maxOpeningWithNeighbour(design: Design, index: number, construction: ConstructionProfile = CONSTRUCTION, bay = 0): { max: number; partnerIndex: number } | null {
  const g = layoutOf(design, bay).openings;
  if (index < 0 || index >= g.length || g.length < 2) return null;
  const j = index < g.length - 1 ? index + 1 : index - 1;
  return { max: g[index]! + g[j]! - construction.envelope.minOpeningTicks, partnerIndex: j };
}

/**
 * Move shelf `shelfNo` (1-based, bottom-up) of a bay so its underside sits at `undersideTicks`
 * from the floor. The openings either side are rebalanced; others are untouched.
 */
export function setShelfDatum(design: Design, shelfNo: number, undersideTicks: number, opts: ChangeOptions = {}): Design {
  const bay = opts.bay ?? 0;
  const t = thicknessOf(stockFor(design, opts.stock));
  const lay = layoutOf(design, bay);
  const i = shelfNo - 1;
  if (i < 0 || i >= lay.n) return cloneDesign(design);
  let below = liftTicks(design) + t; // underside of opening i = top face of the panel below
  for (let k = 0; k < i; k++) below += lay.openings[k]! + t;
  return setOpening(design, i, undersideTicks - below, { ...opts, rebalance: 'neighbour' });
}

/** Turn the "even" convenience on (redistributes) or off (keeps the current openings). */
export function setEven(design: Design, on: boolean, stock: AnyStock = STOCK, bay = 0): Design {
  const lay = layoutOf(design, bay);
  const openings = on ? evenOpenings(bayAvailableTicks(design, bay, stock), lay.n + 1) : lay.openings;
  return withLayout(design, bay, { n: lay.n, even: !!on, openings });
}

/** Change the number of fixed shelves in a bay; its openings are redistributed evenly. */
export function setShelfCount(design: Design, n: number, stock: AnyStock = STOCK, bay = 0): Design {
  const count = Math.max(0, Math.round(n));
  const probe = withLayout(design, bay, { n: count, even: true, openings: [] });
  const A = bayAvailableTicks(probe, bay, stock);
  return withLayout(design, bay, { n: count, even: true, openings: evenOpenings(Math.max(0, A), count + 1) });
}

/** Rescale every bay's openings to its new available height (even stays even; uneven scales, residual lowest). */
function refitOpenings(prev: Design, next: Design, stock?: AnyStock | null): Design {
  let d = next;
  for (let bay = 0; bay < bayCount(next); bay++) {
    const lay = layoutOf(next, bay);
    const A = bayAvailableTicks(next, bay, stock);
    let openings: number[];
    if (lay.even) openings = evenOpenings(Math.max(0, A), lay.n + 1);
    else {
      const old = sum(layoutOf(prev, bay).openings) || 1;
      const scaled = lay.openings.map((g) => Math.floor((g * A) / old));
      if (scaled.length) scaled[0]! += A - sum(scaled);
      openings = scaled;
    }
    d = withLayout(d, bay, { ...lay, openings });
  }
  return d;
}

/** Rescale bay widths to a new total clear width (residual on the first bay). */
function refitBayWidths(prev: Design, next: Design, stock?: AnyStock | null): Design {
  if (bayCount(next) === 1) return next;
  const t = thicknessOf(stockFor(next, stock));
  const total = clearWidthTicks(next, t);
  const old = bayWidthsOf(prev, stock);
  const oldSum = sum(old) || 1;
  const scaled = old.map((w) => Math.floor((w * total) / oldSum));
  scaled[0]! += total - sum(scaled);
  const out = cloneDesign(next);
  out.bayWidths = scaled;
  return out;
}

/**
 * Set W, H or D (ticks). Changing H keeps even bays even; uneven bays scale every opening
 * proportionally and put the rounding residual on the lowest. Changing W rescales bay widths.
 */
export function setDimension(design: Design, key: 'W' | 'H' | 'D', ticks: number, stock: AnyStock = STOCK): Design {
  if (!['W', 'H', 'D'].includes(key)) throw new Error(`Unknown dimension ${String(key)}`);
  const d = cloneDesign(design);
  d[key] = Math.round(ticks);
  if (design.params) return d; // other families keep proportions in params: nothing to refit
  if (key === 'H') return refitOpenings(design, d, stock);
  if (key === 'W') return refitBayWidths(design, d, stock);
  return d;
}

/** Appearance-only changes (never affect the manufacturing hash). */
export function setAppearance(design: Design, patch: Partial<Appearance>): Design {
  const d = cloneDesign(design);
  d.appearance = { ...d.appearance, ...patch };
  for (const k of Object.keys(d.appearance) as (keyof Appearance)[]) if (d.appearance[k] === undefined) delete d.appearance[k];
  return d;
}

/**
 * The remainder fix ("Add 12 mm to the top opening"): places the whole remainder of a bay
 * on its top opening. Returns the new design and a label, or null when nothing is left.
 */
export function placeRemainder(design: Design, stock: AnyStock = STOCK, bay = 0): { design: Design; label: string } | null {
  const lay = layoutOf(design, bay);
  const A = bayAvailableTicks(design, bay, stock);
  const r = A - sum(lay.openings);
  if (!r || !lay.openings.length) return null;
  const g = lay.openings.slice();
  g[g.length - 1]! += r;
  const d = withLayout(design, bay, { n: lay.n, even: false, openings: g });
  const amount = Math.abs(r) / 10;
  const a = Number.isInteger(amount) ? String(amount) : amount.toFixed(1);
  return { design: d, label: r > 0 ? `Add ${a} mm to the top opening` : `Take ${a} mm from the top opening` };
}

// ---------------------------------------------------------------------------------------------
// Bays, back, plinth, joinery, stock
// ---------------------------------------------------------------------------------------------

/** Change the number of bays (1–4). Widths become even; new bays copy bay 1's shelf count, spaced evenly. */
export function setBays(design: Design, count: number, stock?: AnyStock | null): Design {
  const n = Math.max(1, Math.round(count));
  const d = cloneDesign(design);
  if (n === 1) {
    delete d.bays;
    delete d.bayWidths;
    delete d.bayLayouts;
    return d;
  }
  const t = thicknessOf(stockFor(design, stock));
  d.bays = n;
  d.bayWidths = evenOpenings(d.W - (n + 1) * t, n);
  const layouts: BayLayout[] = [];
  for (let i = 1; i < n; i++) {
    const existing = design.bayLayouts?.[i - 1];
    if (existing && i < bayCount(design)) layouts.push({ ...existing, openings: existing.openings.slice() });
    else layouts.push({ n: d.n, even: true, openings: evenOpenings(Math.max(0, bayAvailableTicks(d, 0, stock)), d.n + 1) });
  }
  d.bayLayouts = layouts;
  return d;
}

/** Set one bay's clear width; the bay to its right (left for the last) absorbs the difference. */
export function setBayWidth(design: Design, index: number, ticks: number, construction: ConstructionProfile = CONSTRUCTION, stock?: AnyStock | null): Design {
  const widths = bayWidthsOf(design, stock);
  if (widths.length < 2 || index < 0 || index >= widths.length) return cloneDesign(design);
  const j = index < widths.length - 1 ? index + 1 : index - 1;
  const min = construction.bays?.minBayClearTicks ?? mm(250);
  const total = widths[index]! + widths[j]!;
  const v = Math.max(min, Math.min(total - min, Math.round(ticks)));
  widths[index] = v;
  widths[j] = total - v;
  const d = cloneDesign(design);
  d.bayWidths = widths;
  return d;
}

/** Equal bay widths. */
export function evenBayWidths(design: Design, stock?: AnyStock | null): Design {
  if (bayCount(design) === 1) return cloneDesign(design);
  const d = cloneDesign(design);
  d.bayWidths = evenOpenings(clearWidthTicks(d, thicknessOf(stockFor(d, stock))), bayCount(d));
  return d;
}

export function setBack(design: Design, kind: 'none' | 'overlay' | 'rebated'): Design {
  const d = cloneDesign(design);
  if (kind === 'overlay') delete d.back;
  else d.back = kind;
  return d;
}

/** Plinth height (0 removes it). Openings refit to the new clear height. */
export function setPlinth(design: Design, ticks: number, stock?: AnyStock | null): Design {
  const d = cloneDesign(design);
  const v = Math.max(0, Math.round(ticks));
  if (v) d.plinth = v;
  else delete d.plinth;
  if (v && d.base && d.base !== 'plinth') d.base = 'plinth';
  if (!v && d.base === 'plinth') delete d.base;
  return refitOpenings(design, d, stock);
}

/**
 * What the case stands on (FND-08): none, a plinth (height, set back from the front),
 * levelling feet (height) or sides to the floor (the gap under the raised bottom). Openings refit to the new clear height.
 */
export function setBase(design: Design, base: BaseSpec, stock?: AnyStock | null): Design {
  const d = cloneDesign(design);
  delete d.base; delete d.plinth; delete d.plinthSetback; delete d.feetHeight; delete d.toeHeight;
  if (base.kind === 'plinth') {
    d.plinth = Math.max(0, Math.round(base.height));
    if (base.setback) { d.base = 'plinth'; d.plinthSetback = Math.round(base.setback); }
  } else if (base.kind === 'feet') {
    d.base = 'feet';
    d.feetHeight = Math.round(base.height);
  } else if (base.kind === 'sides') {
    d.base = 'sides';
    d.toeHeight = Math.round(base.height);
  }
  return refitOpenings(design, d, stock);
}

/**
 * One base key from the generic Shape controls (`base`, `plinth`, `plinthSetback`, `feetHeight`,
 * `toeHeight`), kept in step by `setBaseParam` and applied through `setBase` so the openings
 * refit. Null when `key` is not a base key.
 */
export function setGridBaseKey(design: Design, key: string, value: unknown, stock?: AnyStock | null): Design | null {
  if (!(BASE_KEYS as readonly string[]).includes(key)) return null;
  const flat = setBaseParam({ base: gridBaseOf(design).kind, plinth: design.plinth ?? 0, plinthSetback: design.plinthSetback, feetHeight: design.feetHeight, toeHeight: design.toeHeight }, key, value);
  if (!flat) return null;
  const s = design.stockProfileId ? stockById(design.stockProfileId) : null;
  return setBase(design, baseOf(flat, footKindFor(s && isBoardStock(s) ? 'board' : 'sheet')), stock);
}

/** Outer sides as plain panels or vertical slats (FND-13); slat width and gap in ticks. */
export function setSideStyle(design: Design, style: 'panel' | 'slatted', slat?: { width?: number; gap?: number }): Design {
  const d = cloneDesign(design);
  if (style === 'panel') { delete d.sideStyle; delete d.slatWidth; delete d.slatGap; return d; }
  d.sideStyle = 'slatted';
  if (slat?.width) d.slatWidth = Math.round(slat.width);
  if (slat?.gap) d.slatGap = Math.round(slat.gap);
  return d;
}

export function setJoinery(design: Design, kind: 'screwed-butt' | 'housed-dado'): Design {
  const d = cloneDesign(design);
  if (kind === 'screwed-butt') delete d.joinery;
  else d.joinery = kind;
  return d;
}

/** Change the stock (material). Thickness changes refit the openings and bay widths. */
export function setStock(design: Design, stockId: string): Design {
  const next = stockById(stockId);
  if (!next) return cloneDesign(design);
  const d = cloneDesign(design);
  d.stockProfileId = next.id;
  if (design.params) return d;
  return refitBayWidths(design, refitOpenings(design, d, next), next);
}

/** Envelope limits of a construction profile (re-exported for convenience). */
export { limitsOf };
export type { StockProfile };
